import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useId, type ReactNode } from 'react';
import type {
  BlogPostStatus,
  BlogTopicStatus,
  CampaignStatus,
  EmailStatus,
  JobStatus,
  LeadStatus,
} from '../../types/api';
import { cn, formatNumber, titleCase } from '../../lib/utils';
import { type BadgeTone, Button, StatusPill } from './primitives';

// ---------------------------------------------------------------------------
// Status badges — one mapping, used everywhere, so a status always looks the same
// ---------------------------------------------------------------------------

const LEAD_TONES: Record<LeadStatus, BadgeTone> = {
  NEW: 'neutral',
  ENRICHED: 'brand',
  QUEUED: 'warning',
  DRAFTED: 'warning',
  SCHEDULED: 'warning',
  CONTACTED: 'brand',
  REPLIED: 'positive',
  INTERESTED: 'positive',
  WON: 'positive',
  LOST: 'muted',
  BOUNCED: 'danger',
  UNSUBSCRIBED: 'danger',
  DISQUALIFIED: 'muted',
};

const EMAIL_TONES: Record<EmailStatus, BadgeTone> = {
  DRAFT: 'neutral',
  SCHEDULED: 'warning',
  SENDING: 'warning',
  SENT: 'positive',
  FAILED: 'danger',
  CANCELLED: 'muted',
  RECEIVED: 'brand',
};

const JOB_TONES: Record<JobStatus, BadgeTone> = {
  PENDING: 'warning',
  CLAIMED: 'brand',
  DONE: 'positive',
  FAILED: 'danger',
  CANCELLED: 'muted',
};

const CAMPAIGN_TONES: Record<CampaignStatus, BadgeTone> = {
  QUEUED: 'warning',
  RUNNING: 'brand',
  PAUSED: 'neutral',
  DONE: 'positive',
  CANCELLED: 'muted',
};

const TOPIC_TONES: Record<BlogTopicStatus, BadgeTone> = {
  PENDING: 'neutral',
  QUEUED: 'warning',
  DRAFTING: 'warning',
  DRAFTED: 'brand',
  PUBLISHED: 'positive',
  SKIPPED: 'muted',
  ARCHIVED: 'muted',
};

const POST_TONES: Record<BlogPostStatus, BadgeTone> = {
  DRAFT: 'neutral',
  SCHEDULED: 'warning',
  PUBLISHING: 'warning',
  PUBLISHED: 'positive',
  SUPERSEDED: 'muted',
  FAILED: 'danger',
  CANCELLED: 'muted',
};

export const LeadStatusBadge = ({ status }: { status: string }) => (
  <StatusPill tone={LEAD_TONES[status as LeadStatus] ?? 'neutral'}>{titleCase(status)}</StatusPill>
);

export const EmailStatusBadge = ({ status }: { status: string }) => (
  <StatusPill tone={EMAIL_TONES[status as EmailStatus] ?? 'neutral'}>{titleCase(status)}</StatusPill>
);

export const JobStatusBadge = ({ status }: { status: string }) => (
  <StatusPill tone={JOB_TONES[status as JobStatus] ?? 'neutral'}>{titleCase(status)}</StatusPill>
);

export const CampaignStatusBadge = ({ status }: { status: string }) => (
  <StatusPill tone={CAMPAIGN_TONES[status as CampaignStatus] ?? 'neutral'}>
    {titleCase(status)}
  </StatusPill>
);

export const BlogTopicStatusBadge = ({ status }: { status: string }) => (
  <StatusPill tone={TOPIC_TONES[status as BlogTopicStatus] ?? 'neutral'}>
    {titleCase(status)}
  </StatusPill>
);

export const BlogPostStatusBadge = ({ status }: { status: string }) => (
  <StatusPill tone={POST_TONES[status as BlogPostStatus] ?? 'neutral'}>
    {titleCase(status)}
  </StatusPill>
);

// ---------------------------------------------------------------------------
// Stat tile
// ---------------------------------------------------------------------------

export const StatCard = ({
  label,
  value,
  sub,
  icon,
  tone = 'neutral',
  onClick,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  icon?: ReactNode;
  tone?: 'neutral' | 'brand' | 'positive' | 'warning';
  onClick?: () => void;
}) => {
  const chip = {
    neutral: 'bg-raised text-faint',
    brand: 'bg-brand/10 text-brand',
    positive: 'bg-positive/10 text-positive',
    warning: 'bg-warning/10 text-warning',
  }[tone];

  return (
    <div
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(event) => {
        if (onClick && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          onClick();
        }
      }}
      className={cn(
        'group relative rounded-panel border border-line bg-surface p-5 shadow-card',
        onClick &&
          'cursor-pointer transition-[border-color,box-shadow] duration-150 hover:border-line-strong hover:shadow-raise',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-[13px] font-medium text-muted">{label}</span>
        {icon ? (
          <span className={cn('flex h-6 w-6 shrink-0 items-center justify-center rounded-md', chip)}>
            {icon}
          </span>
        ) : null}
      </div>
      <div className="mt-3 font-display text-[30px] font-semibold leading-none tracking-tight tabular-nums text-ink">
        {value}
      </div>
      {sub ? <div className="mt-2 text-xs text-muted">{sub}</div> : null}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sparkline — two series, no chart library
// ---------------------------------------------------------------------------

export const Sparkline = ({
  data,
  height = 64,
  series,
}: {
  data: Array<Record<string, number | string>>;
  height?: number;
  series: Array<{ key: string; label: string; color: string }>;
}) => {
  // React's useId contains colons, which are awkward inside url(#…) references.
  const gradientId = useId().replace(/[^a-zA-Z0-9]/g, '');
  if (!data.length) return null;
  const width = 640;
  const max = Math.max(
    1,
    ...data.flatMap((point) => series.map((entry) => Number(point[entry.key] ?? 0))),
  );
  const stepX = data.length > 1 ? width / (data.length - 1) : width;
  // Every series flat at zero draws a line along the floor, which looks like a bug rather
  // than a quiet fortnight. Say so instead.
  const allZero = data.every((point) => series.every((entry) => !Number(point[entry.key] ?? 0)));

  const pointAt = (key: string, index: number): [number, number] => [
    index * stepX,
    height - (Number(data[index][key] ?? 0) / max) * (height - 8) - 4,
  ];

  const pathFor = (key: string): string =>
    data
      .map((_, index) => {
        const [x, y] = pointAt(key, index);
        return `${index === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' ');

  if (allZero) {
    return (
      <div
        className="flex items-center justify-center rounded-lg border border-dashed border-line text-[13px] text-faint"
        style={{ height }}
      >
        Nothing recorded in this window yet.
      </div>
    );
  }

  return (
    <div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        style={{ height }}
        className="w-full overflow-visible"
        role="img"
        aria-label={series.map((entry) => entry.label).join(' and ')}
      >
        <defs>
          {series.map((entry) => (
            <linearGradient
              key={entry.key}
              id={`${gradientId}-${entry.key}`}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop offset="0%" stopColor={entry.color} stopOpacity="0.18" />
              <stop offset="100%" stopColor={entry.color} stopOpacity="0" />
            </linearGradient>
          ))}
        </defs>
        {series.map((entry) => {
          const [lastX, lastY] = pointAt(entry.key, data.length - 1);
          return (
            <g key={entry.key}>
              <path
                d={`${pathFor(entry.key)} L${width},${height} L0,${height} Z`}
                fill={`url(#${gradientId}-${entry.key})`}
              />
              <path
                d={pathFor(entry.key)}
                fill="none"
                stroke={entry.color}
                strokeWidth="1.75"
                strokeLinejoin="round"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
              />
              {/* The most recent value is the one being watched, so it gets a marker. */}
              <circle
                cx={lastX}
                cy={lastY}
                r="3"
                fill={entry.color}
                stroke="rgb(var(--surface))"
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
              />
            </g>
          );
        })}
      </svg>
      <div className="mt-3 flex flex-wrap items-center gap-4 border-t border-line pt-3">
        {series.map((entry) => (
          <span key={entry.key} className="flex items-center gap-1.5 text-xs text-muted">
            <span className="h-2 w-2 rounded-full" style={{ background: entry.color }} />
            {entry.label}
            <span className="font-display font-semibold tabular-nums text-ink">
              {formatNumber(
                data.reduce((total, point) => total + Number(point[entry.key] ?? 0), 0),
              )}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------

export const Pagination = ({
  page,
  totalPages,
  total,
  pageSize,
  onPage,
}: {
  page: number;
  totalPages: number;
  total: number;
  pageSize: number;
  onPage: (page: number) => void;
}) => {
  if (total === 0) return null;
  const first = (page - 1) * pageSize + 1;
  const last = Math.min(total, page * pageSize);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-5 py-3 text-[13px] text-muted">
      <span className="tabular">
        <span className="font-medium text-ink">
          {formatNumber(first)}–{formatNumber(last)}
        </span>{' '}
        of {formatNumber(total)}
      </span>
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          icon={<ChevronLeft className="h-4 w-4" />}
        >
          Previous
        </Button>
        <span className="px-2 text-xs tabular-nums text-faint">
          {page} / {totalPages}
        </span>
        <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>
          Next
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Filter chips
// ---------------------------------------------------------------------------

export const FilterChips = <T extends string>({
  options,
  value,
  onChange,
  counts,
}: {
  options: Array<{ value: T; label: string }>;
  value: T | undefined;
  onChange: (value: T | undefined) => void;
  counts?: Record<string, number>;
}) => (
  <div className="flex flex-wrap items-center gap-1.5">
    {[{ value: undefined, label: 'All' }, ...options].map((option) => {
      const active = value === option.value;
      const count = option.value === undefined ? undefined : counts?.[option.value];
      return (
        <button
          key={option.value ?? 'all'}
          type="button"
          aria-pressed={active}
          onClick={() => onChange(active ? undefined : option.value)}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors duration-100',
            active
              ? 'border-brand bg-brand text-brand-ink shadow-card'
              : 'border-line bg-surface text-muted shadow-card hover:border-line-strong hover:text-ink',
          )}
        >
          {option.label}
          {count !== undefined ? (
            <span className={cn('tabular-nums', active ? 'opacity-75' : 'text-faint')}>{count}</span>
          ) : null}
        </button>
      );
    })}
  </div>
);
